/**
 * Citation enrichment for Chat with your docs (DEV-6130), ported from the web
 * app's chat-component/citationEnrichment.ts.
 *
 * Backend citations can arrive without a usable source: when Ask Addy answers
 * from its own document tools instead of the attached text, the citation has no
 * document name or URL and its excerpt is raw, JSON-escaped OCR ("\n", "##").
 * Ask Addy holds the picked documents' text, so each citation is matched back
 * to the picked document that contains the cited value, and gets that
 * document's name and URL, a readable excerpt and a verbatim search term the
 * PDF viewer can highlight.
 */

const MAX_EXCERPT_LENGTH = 220;
// Characters of context kept on each side of the cited value.
const EXCERPT_RADIUS = 70;
const SEARCH_RADIUS = 28;
// Exact amounts, dates and long numbers are far stronger evidence than a word.
const ANCHOR_WEIGHT = 3;
// Minimum passage score before a match replaces what the backend sent.
const MIN_CONFIDENCE_SCORE = 3;
const MIN_KEYWORD_LENGTH = 4;

const uniqueNonEmpty = (values) => [...new Set(values.map((value) => value.trim()).filter(Boolean))];

/** Turns JSON-escaped text ("\\n", "\\"") back into plain lines. */
const unescapeJsonText = (text = "") =>
  text
    .replace(/\\r\\n|\\n|\\r/g, "\n")
    .replace(/\\t/g, " ")
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, "\\");

const stripMarkdownHeadings = (text = "") => text.replace(/^[ \t]*#{1,6}[ \t]*/gm, "");

/**
 * Splits stored document text into OCR prose and key-value lines. Stored text
 * is usually a JSON envelope ({ ocr, keyValuePairs, tables }), sometimes cut off
 * mid-JSON by the 10,000-character limit, in which case its escaped text is
 * recovered line by line.
 */
const parseDocText = (raw = "") => {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return { ocrText: unescapeJsonText(trimmed), kvText: "" };
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return { ocrText: parsed.map(String).join("\n"), kvText: "" };
    }

    const { keyValuePairs = [], ocr = [] } = parsed || {};
    const kvText = (Array.isArray(keyValuePairs) ? keyValuePairs : [])
      .filter((pair) => pair?.key && pair?.value != null)
      .map(({ key, value }) =>
        `${String(key).trim()}: ${typeof value === "object" ? JSON.stringify(value) : String(value).trim()}`
      )
      .join("\n");
    const ocrText = (Array.isArray(ocr) ? ocr : []).map(String).join("\n");

    return { ocrText, kvText };
  } catch {
    // Truncated envelope: keep the readable text, drop the JSON syntax.
    const recovered = unescapeJsonText(trimmed)
      .replace(/"(ocr|keyValuePairs|tables|key|value|content|name)"\s*:/g, "\n")
      .replace(/[{}[\]"]/g, " ")
      .replace(/\[truncated\]\s*$/, "");

    return { ocrText: recovered, kvText: "" };
  }
};

/** Readable text for an excerpt or search term that may be escaped OCR. */
export const toReadableCitationText = (raw = "") =>
  stripMarkdownHeadings(unescapeJsonText(raw || ""))
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

/** Strongest locatable values in a citation: amounts, dates, a Key: Value value, long numbers. */
const extractAnchors = ({ searchTerm = "", excerpt = "" }) => {
  const source = `${searchTerm}\n${excerpt}`;
  const dollarAmounts = source.match(/\$[\d,]+(?:\.\d{1,2})?/g) || [];
  const dates = source.match(/\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/g) || [];
  const longNumbers = source.match(/\b\d{3,}(?:,\d{3})*(?:\.\d+)?\b/g) || [];
  const trimmedExcerpt = excerpt.trim();
  const colonIndex = trimmedExcerpt.indexOf(":");
  const valueAfterColon =
    colonIndex > 0 && colonIndex < 48 ? trimmedExcerpt.slice(colonIndex + 1).trim().slice(0, 60) : "";

  return uniqueNonEmpty([
    ...dollarAmounts,
    ...dates,
    ...(valueAfterColon.length >= 3 ? [valueAfterColon] : []),
    ...longNumbers,
  ]);
};

const extractKeywords = ({ searchTerm = "", excerpt = "" }) =>
  uniqueNonEmpty(
    `${searchTerm} ${excerpt}`
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter((word) => word.length >= MIN_KEYWORD_LENGTH)
  );

/** A window of `text` around a match, cut at word boundaries, whitespace collapsed. */
const extractWindow = (text, matchIndex, matchLength, radius) => {
  const rawStart = Math.max(0, matchIndex - radius);
  const rawEnd = Math.min(text.length, matchIndex + matchLength + radius);
  const startBoundary = rawStart > 0 ? text.indexOf(" ", rawStart) : -1;
  const endBoundary = rawEnd < text.length ? text.lastIndexOf(" ", rawEnd) : -1;
  const start = startBoundary !== -1 && startBoundary < matchIndex ? startBoundary + 1 : rawStart;
  const end = endBoundary !== -1 && endBoundary > matchIndex + matchLength ? endBoundary : rawEnd;

  return {
    snippet: text.slice(start, end).replace(/\s+/g, " ").trim(),
    atStart: start === 0,
    atEnd: end === text.length,
  };
};

const truncate = (text, max) => (text.length <= max ? text : `${text.slice(0, max).trim()}...`);

const scoreLine = (line, anchorsLower, keywordsLower) => {
  const lower = line.toLowerCase();
  const anchorIndexes = anchorsLower.map((anchor) => ({ anchor, index: lower.indexOf(anchor) }));
  const keywordIndexes = keywordsLower.map((keyword) => ({ keyword, index: lower.indexOf(keyword) }));
  const anchorHits = anchorIndexes.filter(({ index }) => index !== -1);
  const keywordHits = keywordIndexes.filter(({ index }) => index !== -1);
  const [firstAnchor] = anchorHits;
  const [firstKeyword] = keywordHits;

  return {
    score: anchorHits.length * ANCHOR_WEIGHT + keywordHits.length,
    anchorHits: anchorHits.length,
    keywordHits: keywordHits.length,
    index: firstAnchor?.index ?? firstKeyword?.index ?? 0,
    matchLength: firstAnchor ? firstAnchor.anchor.length : 0,
  };
};

/** Best-scoring line of `text` for the citation, with whether the top score was tied. */
const locateBestPassage = (text, anchors, keywords) => {
  const lines = (text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const anchorsLower = anchors.map((anchor) => anchor.toLowerCase());
  const keywordsLower = keywords.map((keyword) => keyword.toLowerCase());

  const scoredLines = lines
    .map((line) => ({ line, ...scoreLine(line, anchorsLower, keywordsLower) }))
    .filter(({ score }) => score > 0);
  if (scoredLines.length === 0) {
    return null;
  }

  const best = scoredLines.reduce((top, candidate) => (candidate.score > top.score ? candidate : top));
  const tieCount = scoredLines.filter(({ score }) => score === best.score).length;
  const display = extractWindow(best.line, best.index, best.matchLength, EXCERPT_RADIUS);
  const search = extractWindow(best.line, best.index, best.matchLength, SEARCH_RADIUS);
  const excerpt = `${display.atStart ? "" : "..."}${display.snippet}${display.atEnd ? "" : "..."}`;

  return {
    excerpt: truncate(excerpt, MAX_EXCERPT_LENGTH),
    searchTerm: search.snippet || anchors[0] || "",
    score: best.score,
    anchorHits: best.anchorHits,
    keywordHits: best.keywordHits,
    ambiguous: tieCount > 1,
  };
};

const isHighConfidence = ({ score, keywordHits, anchorHits, ambiguous }) => {
  if (score < MIN_CONFIDENCE_SCORE) {
    return false;
  }

  return keywordHits >= 2 || (anchorHits >= 1 && (keywordHits >= 1 || !ambiguous));
};

/** Best passage for a citation in one document (OCR prose first, then key-value lines). */
const findBestPassageInDocument = (citation, document) => {
  const anchors = extractAnchors(citation);
  const keywords = extractKeywords(citation);
  const { ocrText, kvText } = parseDocText(document.textContent || "");
  const candidates = [
    locateBestPassage(ocrText, anchors, keywords),
    locateBestPassage(kvText, anchors, keywords),
  ].filter(Boolean);

  return candidates.sort((first, second) => second.score - first.score)[0] || null;
};

/** The picked document a citation names (id, URL, or a unique file name). */
const findNamedDocument = (citation, documents) => {
  const { documentId = "", documentUrl = "", documentName = "" } = citation;
  const byId = documentId && documents.find(({ id }) => id === documentId);
  if (byId) {
    return byId;
  }
  const byUrl = documentUrl && documents.find(({ url }) => url === documentUrl);
  if (byUrl) {
    return byUrl;
  }

  const targetName = documentName.trim().toLowerCase();
  const byName = targetName
    ? documents.filter(({ name = "" }) => name.trim().toLowerCase() === targetName)
    : [];

  return byName.length === 1 ? byName[0] : null;
};

/**
 * The picked document that holds the cited value, for citations that do not
 * name one. Only a clear, single best match is used, so a citation is never
 * pinned on the wrong document.
 */
const findDocumentByContent = (citation, documents) => {
  const matches = documents
    .map((document) => ({ document, passage: findBestPassageInDocument(citation, document) }))
    .filter(({ passage }) => passage && isHighConfidence(passage))
    .sort((first, second) => second.passage.score - first.passage.score);
  const [best, runnerUp] = matches;
  if (!best || (runnerUp && runnerUp.passage.score === best.passage.score)) {
    return null;
  }

  return best.document;
};

/**
 * @param {Object} citation - Backend citation ({ number, documentName, documentUrl, excerpt, searchTerm, ... })
 * @param {Array<{ id: string, name: string, url: string, textContent: string }>} documents - Picked documents with text
 */
export const enrichCitation = (citation, documents = []) => {
  const readableCitation = {
    ...citation,
    excerpt: truncate(toReadableCitationText(citation.excerpt), MAX_EXCERPT_LENGTH),
    searchTerm: toReadableCitationText(citation.searchTerm),
  };
  // The backend can name a document by guessing (its fallback is the first one),
  // so a named document that does not hold the cited value gives way to the
  // picked document that clearly does.
  const namedDocument = findNamedDocument(readableCitation, documents);
  const namedPassage = namedDocument && findBestPassageInDocument(readableCitation, namedDocument);
  const isNamedDocumentConfirmed = Boolean(namedPassage && isHighConfidence(namedPassage));
  const document = isNamedDocumentConfirmed
    ? namedDocument
    : findDocumentByContent(readableCitation, documents) || namedDocument;
  if (!document) {
    return readableCitation;
  }

  const passage =
    document === namedDocument ? namedPassage : findBestPassageInDocument(readableCitation, document);
  const sourceFields = {
    documentId: document.id,
    documentName: document.name,
    documentUrl: document.url || readableCitation.documentUrl,
  };

  return passage && isHighConfidence(passage)
    ? { ...readableCitation, ...sourceFields, excerpt: passage.excerpt, searchTerm: passage.searchTerm }
    : { ...readableCitation, ...sourceFields };
};

export const enrichCitations = (citations = [], documents = []) =>
  citations.map((citation) => enrichCitation(citation, documents));
